// MindBook Service (CMS) — spricht /api/mindbook (Muster: audioService). Bearer aus der Supabase-Session.
import axios, { AxiosInstance } from 'axios';
import { MindBookContent, MindBookFilters, MindBookFormData, PaginatedResponse, ApiPagination } from '../types/mindbook';
import { normalizePagination } from '../types';
import { getApiBaseUrl } from '../utils/apiConfig';
import { getAccessToken } from '../lib/authToken';
import type { FassungsUebersicht } from '../types/fassungen';

class MindBookService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: getApiBaseUrl(),
      timeout: 15000,
      headers: { 'Content-Type': 'application/json' },
    });
    this.api.interceptors.request.use(async (config) => {
      const token = await getAccessToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  }

  async getMindBooks(filters: MindBookFilters): Promise<PaginatedResponse<MindBookContent>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.append(key, String(value));
    });
    const response = await this.api.get<{ success: boolean; data: { content: MindBookContent[]; pagination: ApiPagination } }>(
      `/api/mindbook?${params.toString()}`
    );
    if (response.data.success && response.data.data) {
      return {
        data: response.data.data.content || [],
        pagination: normalizePagination(response.data.data.pagination),
      };
    }
    return { data: [], pagination: normalizePagination(null) };
  }

  async getMindBookById(id: string): Promise<MindBookContent> {
    const response = await this.api.get<{ success: boolean; data: MindBookContent }>(`/api/mindbook/${id}`);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error('Failed to get mindbook');
  }

  /**
   * Welche Fassungen dieses MindBook traegt — Schwester von `audioService.getFassungen`.
   *
   * ⚠️ Der Pfad heisst `/api/mindbook/...` in der EINZAHL. Die Mehrzahl liefert 404, und ein 404
   * saehe an dieser Stelle aus wie „die Route fehlt" statt „der Pfad ist falsch geschrieben" —
   * genau die Verwechslung, die eine halbe Stunde Suche kostet. `main.ts:255` haengt die Routen
   * unter `/api/mindbook` ein, nicht unter `/api/mindbooks`.
   *
   * ⛔ Wie bei Audio: hier kommt NICHTS zum Abspielen zurueck. Signierte Adressen laufen nach 12 h
   * ab; die holt das CMS bei Bedarf ueber `GET /api/content/:id/wiedergabe`.
   */
  async getFassungen(id: string): Promise<FassungsUebersicht> {
    const response = await this.api.get<{ success: boolean; data: FassungsUebersicht; message?: string }>(
      `/api/mindbook/${id}/fassungen`
    );
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.message || 'Fassungen konnten nicht geladen werden');
  }

  async createMindBook(data: MindBookFormData): Promise<MindBookContent> {
    const response = await this.api.post<{ success: boolean; data: MindBookContent; message?: string }>(
      '/api/mindbook',
      this.toBackendPayload(data)
    );
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.message || 'Failed to create mindbook');
  }

  async updateMindBook(id: string, data: MindBookFormData): Promise<MindBookContent> {
    const response = await this.api.put<{ success: boolean; data: MindBookContent; message?: string }>(
      `/api/mindbook/${id}`,
      this.toBackendPayload(data)
    );
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.message || 'Failed to update mindbook');
  }

  async deleteMindBook(id: string): Promise<void> {
    const response = await this.api.delete<{ success: boolean; message?: string }>(`/api/mindbook/${id}`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to delete mindbook');
  }

  // Formular → Backend-Payload (Feldnamen wie MindBookController.transformFormDataToMindBookData).
  // chapters: undefined = nicht anfassen; Array (auch []) = Kapitel ersetzen.
  private toBackendPayload(d: MindBookFormData) {
    return {
      category: 'mind-book',
      title: d.title || '',
      author: d.author !== undefined ? d.author : '',
      description: d.description !== undefined ? d.description : '',
      shortDescription: d.shortDescription !== undefined ? d.shortDescription : '',
      // ⛔ `longDescription` gibt es nicht mehr (Bereinigung 2026-09-01, CONTRACT in #769).
      //
      // ⚠️ Die Zeile fehlt hier mit Absicht und darf nicht „nachgetragen" werden: hier stand
      // `formData.longDescription !== undefined ? … : ''`. Ein nicht geführtes Feld hätte damit
      // eine leere Zeichenkette geschickt — solange die Spalte noch existierte, war das eine
      // Löschfalle; jetzt wäre es das Wiederanlegen einer dritten Wahrheit. Das Backend hält mit
      // `services/__tests__/longDescriptionAusgetragen.test.ts` dagegen.
      duration: d.duration !== undefined ? d.duration : '',
      keywords: d.keywords !== undefined ? d.keywords : '',
      thumbnailUrl: d.coverImageUrl || '',
      coverImageUrl: d.coverImageUrl || '',
      // ⛔ `subliminalMp3Url` / `plainMp3Url` gehen NICHT mehr raus (Phase 3 „Contract“, 2026-09-01).
      //
      // ⚠️ Die zwei Zeilen fehlen mit Absicht und duerfen nicht „nachgetragen“ werden — dieselbe
      // Falle wie bei `longDescription` daneben, nur andersherum: hier stand `d.x || ''`. Das
      // Formular fuehrt die Felder nicht mehr, also waere `d.x` immer `undefined` und wir
      // schickten bei JEDEM Speichern eine leere Zeichenkette. Das Backend faengt das heute ab
      // (`mindBookMapping` faellt auf den Bestand zurueck), aber sich auf das Wohlwollen eines
      // `||` in einem fremden Dienst zu verlassen, ist keine Zusicherung: aendert dort jemand die
      // Reihenfolge, loescht dieses Formular beim naechsten Titel-Tippfehler zwei Adressen.
      //
      // ⛔ Bestandswerte bleiben liegen. Sie sind tot (der Player liest seit #792 die
      // Wiedergabe-Route), aber ein Loeschlauf waere schreibend auf Bestand und ist NICHT beauftragt.
      markerTimecodeCSVUrl: d.markerTimecodeCSVUrl || '',
      chapters: Array.isArray(d.chapters)
        ? d.chapters.map((c) => ({ title: c.title, startMs: c.startMs }))
        : undefined,
      isPremium: Boolean(d.isPremium),
      tags: Array.isArray(d.tags) ? d.tags : [],
      // ⚠️ Ohne diese Zeile wäre der Themen-Wähler im Formular ein stiller Blindgänger:
      // anhaken, speichern, nichts kommt an. Der Server prüft die Slugs (topicCategoryGuard),
      // bekommen muss er sie aber erst mal.
      topicCategories: Array.isArray(d.topicCategories) ? d.topicCategories : [],
      // Status nur, wenn das Formular ihn führt. Hier stand `status: … || 'DRAFT'` — dieselbe Bauart, die
      // im Backend still depublizierte (`status-fehlt-faellt-auf-draft`): aus „nicht gesendet" wurde
      // „ausdrücklich Entwurf". Ein FEHLENDER Status lässt den Bestand stehen; beim Anlegen setzt das
      // Backend DRAFT selbst. (BACKLOG `cms-status-oder-draft`)
      ...(d.status ? { status: d.status } : {}),
    };
  }
}

export const mindBookService = new MindBookService();
